import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyStateComponent } from '../../../shared/empty-state/empty-state.component';
import { MobileDrawerComponent } from '../../../shared/mobile-drawer/mobile-drawer.component';

export interface DiscoveryStatusOption {
  status_id: string | number;
  status_description: string;
}

export interface DiscoverySeasonOption {
  id: string | number;
  season_name: string;
}

export interface DiscoveryLevelOption {
  label: string;
}

export type DiscoveryFilterTab = 'nearMe' | 'status' | 'season' | 'level' | 'date';

interface DiscoveryFilterTabItem {
  id: DiscoveryFilterTab;
  labelKey: string;
}

@Component({
  selector: 'stupa-event-discovery-filter-drawer',
  standalone: true,
  imports: [TranslateModule, UpperCasePipe, MobileDrawerComponent, EmptyStateComponent],
  templateUrl: './event-discovery-filter-drawer.component.html',
  styleUrl: './event-discovery-filter-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventDiscoveryFilterDrawerComponent {
  readonly visible = input(false);
  readonly isBroadcast = input(false);
  readonly nearMe = input(false);
  readonly dateSelected = input(false);
  readonly statuses = input<DiscoveryStatusOption[]>([]);
  readonly seasons = input<DiscoverySeasonOption[]>([]);
  readonly levels = input<DiscoveryLevelOption[]>([]);
  readonly selectedStatus = input<string[]>([]);
  readonly selectedSeason = input<string[]>([]);
  readonly selectedLevel = input<string[]>([]);

  readonly closed = output<void>();
  readonly applied = output<void>();
  readonly cleared = output<void>();
  readonly nearMeChange = output<boolean>();
  readonly statusToggle = output<string>();
  readonly seasonToggle = output<string>();
  readonly levelToggle = output<string>();

  readonly activeTab = signal<DiscoveryFilterTab>('status');
  readonly query = signal('');
  private drawerWasOpen = false;

  readonly tabs = computed<DiscoveryFilterTabItem[]>(() => {
    const items: DiscoveryFilterTabItem[] = [
      { id: 'nearMe', labelKey: 'near_me' },
      { id: 'status', labelKey: 'event_status' },
    ];
    if (!this.isBroadcast()) {
      items.push(
        { id: 'season', labelKey: 'season' },
        { id: 'level', labelKey: 'event_level' },
      );
    }
    items.push({ id: 'date', labelKey: 'select_date' });
    return items;
  });

  readonly showSearch = computed(() => {
    const tab = this.activeTab();
    return tab === 'status' || tab === 'season' || tab === 'level';
  });

  readonly searchPlaceholderKey = computed(() => {
    const tab = this.activeTab();
    if (tab === 'season') {
      return 'search_seasons';
    }
    if (tab === 'level') {
      return 'search_event_levels';
    }
    return 'search_event_statuses';
  });

  readonly hintKey = computed(() => {
    const tab = this.activeTab();
    if (tab === 'nearMe') {
      return 'near_me';
    }
    if (tab === 'season') {
      return 'season';
    }
    if (tab === 'level') {
      return 'event_level';
    }
    if (tab === 'date') {
      return 'select_range_to_filter';
    }
    return 'event_status';
  });

  readonly totalCount = computed(() =>
    this.tabs().reduce((sum, tab) => sum + this.tabCount(tab.id), 0),
  );

  constructor() {
    effect(() => {
      const open = this.visible();
      if (open && !this.drawerWasOpen) {
        this.activeTab.set('status');
        this.query.set('');
      }
      this.drawerWasOpen = open;
    });
  }

  tabCount(id: DiscoveryFilterTab): number {
    if (id === 'nearMe') {
      return this.nearMe() ? 1 : 0;
    }
    if (id === 'status') {
      return this.selectedStatus().length;
    }
    if (id === 'season') {
      return this.selectedSeason().length;
    }
    if (id === 'level') {
      return this.selectedLevel().length;
    }
    return this.dateSelected() ? 1 : 0;
  }

  setTab(tab: DiscoveryFilterTab): void {
    this.activeTab.set(tab);
    this.query.set('');
  }

  onQueryInput(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      this.query.set(target.value);
    }
  }

  clearQuery(): void {
    this.query.set('');
  }

  filteredStatuses(): DiscoveryStatusOption[] {
    return this.filterByQuery(this.statuses(), (status) => status.status_description ?? '');
  }

  filteredSeasons(): DiscoverySeasonOption[] {
    return this.filterByQuery(this.seasons(), (season) => season.season_name ?? '');
  }

  filteredLevels(): DiscoveryLevelOption[] {
    return this.filterByQuery(this.levels(), (level) => level.label ?? '');
  }

  emptyPurpose(): 'no-records' | 'no-results' {
    return this.query().trim() ? 'no-results' : 'no-records';
  }

  statusValue(status: DiscoveryStatusOption): string {
    return String(status.status_id);
  }

  seasonValue(season: DiscoverySeasonOption): string {
    return String(season.id);
  }

  isStatusSelected(status: DiscoveryStatusOption): boolean {
    return this.selectedStatus().includes(this.statusValue(status));
  }

  isSeasonSelected(season: DiscoverySeasonOption): boolean {
    return this.selectedSeason().includes(this.seasonValue(season));
  }

  isLevelSelected(level: DiscoveryLevelOption): boolean {
    return this.selectedLevel().includes(level.label);
  }

  levelDomId(level: DiscoveryLevelOption): string {
    return level.label.replace(/[^\w-]/g, '_');
  }

  onNearMeChange(event: Event): void {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      this.nearMeChange.emit(target.checked);
    }
  }

  onTabKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const forward = key === 'ArrowDown' || key === 'ArrowRight';
    const backward = key === 'ArrowUp' || key === 'ArrowLeft';
    if (!forward && !backward && key !== 'Home' && key !== 'End') {
      return;
    }
    event.preventDefault();
    const tabs = this.tabs();
    const current = tabs.findIndex((tab) => tab.id === this.activeTab());
    const next = this.nextTabIndex(current, tabs.length, key);
    const tab = tabs[next];
    if (!tab) {
      return;
    }
    this.setTab(tab.id);
    queueMicrotask(() => {
      document.getElementById(`st_events_discovery_filter_tab_${tab.id}`)?.focus();
    });
  }

  clearFilters(): void {
    this.query.set('');
    this.cleared.emit();
  }

  private nextTabIndex(current: number, length: number, key: string): number {
    if (key === 'Home') {
      return 0;
    }
    if (key === 'End') {
      return length - 1;
    }
    if (key === 'ArrowDown' || key === 'ArrowRight') {
      return current < 0 ? 0 : (current + 1) % length;
    }
    return current <= 0 ? length - 1 : current - 1;
  }

  private filterByQuery<T>(options: readonly T[], labelOf: (item: T) => string): T[] {
    const query = this.query().trim().toLowerCase();
    if (!query) {
      return [...options];
    }
    return options.filter((item) => labelOf(item).toLowerCase().includes(query));
  }
}
